import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Power, Bot } from 'lucide-react';
import { MarketsView } from '../../components/MarketsView';
import { Panel, Empty, Disclaimer, Badge } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { api, errMsg, fmtUsd, fmtNum } from '../../lib/api';

function OrderBook() {
  const [book, setBook] = useState({ bids: [], asks: [] });
  useEffect(() => {
    const gen = () => {
      const mid = 100 + Math.random() * 5;
      const mk = (dir) => Array.from({ length: 8 }).map((_, i) => ({
        price: (mid + dir * (i + 1) * 0.12).toFixed(2),
        size: (Math.random() * 4 + 0.2).toFixed(3),
      }));
      setBook({ bids: mk(-1), asks: mk(1).reverse() });
    };
    gen();
    const id = setInterval(gen, 2500);
    return () => clearInterval(id);
  }, []);
  const Row = ({ r, tone }) => (
    <div className="flex justify-between text-xs font-mono py-0.5">
      <span className={tone}>{r.price}</span><span className="text-vj-grey">{r.size}</span>
    </div>
  );
  return (
    <Panel title="Order Book" testId="order-book" action={<Badge tone="gold">Demonstration data</Badge>}>
      <div className="space-y-0.5">
        {book.asks.map((r, i) => <Row key={'a' + i} r={r} tone="text-vj-red" />)}
        <div className="text-center text-xs text-vj-grey py-1 border-y border-border my-1">Spread</div>
        {book.bids.map((r, i) => <Row key={'b' + i} r={r} tone="text-vj-neon" />)}
      </div>
      <p className="text-[11px] text-vj-grey mt-3">Illustrative depth, not VEYJEX execution liquidity. Live order entry is enabled only when an exchange adapter is configured.</p>
    </Panel>
  );
}

export default function Trading() {
  const { data: bot, reload } = useFetch('/bot/status', [], 30000);
  const { data: orders, reload: reloadOrders } = useFetch('/orders', [], 30000);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/bot/toggle');
      toast.success(`S.I. Bot ${data.active ? 'activated (paper)' : 'paused'}`);
      reload(); reloadOrders();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="trading-terminal">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2"><MarketsView embedded /></div>
        <div className="space-y-6">
          <Panel title="S.I. Bot Execution" testId="trading-bot-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full border border-vj-neon/40 grid place-items-center"><Bot size={20} className="text-vj-neon" /></div>
              <div><div className="font-display">{bot?.status || 'Offline'}</div><div className="text-xs text-vj-grey">{(bot?.mode || 'paper').toUpperCase()} MODE</div></div>
            </div>
            <button onClick={toggle} disabled={busy} data-testid="bot-toggle-btn"
              className={`w-full h-11 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all ${bot?.active ? 'border border-vj-red/40 text-vj-red hover:bg-vj-red/10' : 'bg-vj-neon text-vj-obsidian vj-glow-green hover:-translate-y-0.5'}`}>
              <Power size={16} /> {bot?.active ? 'Pause Bot' : 'Activate Bot (Paper)'}
            </button>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-vj-grey">Allocation</div><div className="font-mono">{fmtUsd(bot?.allocation)}</div></div>
              <div><div className="text-xs text-vj-grey">Realized P&L</div><div className={`font-mono ${Number(bot?.realized_pnl) >= 0 ? 'text-vj-neon' : 'text-vj-red'}`}>{fmtUsd(bot?.realized_pnl)}</div></div>
            </div>
          </Panel>
          <OrderBook />
        </div>
      </div>

      <Panel title="Recent Trades (Paper)" testId="trading-recent">
        {orders && orders.length ? (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-vj-grey border-b border-border"><th className="py-2 font-medium">Symbol</th><th className="font-medium">Side</th><th className="font-medium">Qty</th><th className="font-medium">P&L</th><th className="font-medium">Fee</th></tr></thead>
            <tbody>{orders.slice(0, 12).map((o) => (
              <tr key={o.id} className="border-b border-border last:border-0">
                <td className="py-2.5 font-mono">{o.symbol}</td>
                <td className="capitalize">{o.side}</td>
                <td className="font-mono">{fmtNum(o.qty, 4)}</td>
                <td className={`font-mono ${Number(o.pnl) >= 0 ? 'text-vj-neon' : 'text-vj-red'}`}>{fmtUsd(o.pnl)}</td>
                <td className="font-mono text-vj-grey">{fmtUsd(o.fee)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        ) : <Empty>No trades yet. Activate the S.I. Bot to run paper trades.</Empty>}
      </Panel>

      <Disclaimer>All trades shown are PAPER TRADING results for simulation and never represent real executed exchange orders. Live execution requires a configured, tested exchange adapter.</Disclaimer>
    </div>
  );
}
